import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { unwrap, type ObjectResponse } from './envelope';

/**
 * Token bundle the auth slice persists. Mirrors the response shape from
 * `/auth/login` and `/auth/refresh-token` (defined in `auth.service.ts`).
 */
export interface ClientAuthSession {
  accessToken: string;
  refreshToken: string;
  /** Absolute ISO datetime when accessToken expires. */
  expiresAt: string;
  tenantId: string;
  sessionId: string;
}

export type SessionGetter = () => ClientAuthSession | null | undefined;
export type SessionSetter = (session: ClientAuthSession) => void;
export type UnauthorizedHandler = () => void;

export interface ApiClientOptions {
  baseURL?: string;
  /** Returns the active session (or null when logged-out). */
  getSession?: SessionGetter;
  /** Persists a refreshed session back to the store. */
  setSession?: SessionSetter;
  /** Called when refresh is impossible (no refresh token, refresh failed, etc.). */
  onUnauthorized?: UnauthorizedHandler;
  /** Proactive-refresh window in seconds. Default 120 (2 min). */
  refreshLeewaySeconds?: number;
}

/* ------------------------------------------------------------------------ */
/* Internal helpers                                                         */
/* ------------------------------------------------------------------------ */

function isExpiringSoon(expiresAt: string, leewaySec: number): boolean {
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return false;
  return t - Date.now() < leewaySec * 1000;
}

function isExpired(expiresAt: string): boolean {
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return false;
  return t <= Date.now();
}

interface RetryFlag {
  _retry?: boolean;
  _skipAuth?: boolean;
}
type RetryConfig = InternalAxiosRequestConfig & RetryFlag;

/**
 * Pull the most useful failure message out of whatever shape the
 * backend returned. Tries (in order):
 *  1. `ObjectResponse.message` — our standard envelope.
 *  2. ASP.NET ProblemDetails `title` / `detail`.
 *  3. ModelState validation dictionary `errors[field][0]`.
 *  4. Plain string body.
 *  5. axios's own `err.message` ("Request failed with status code 400").
 */
function extractBackendMessage(err: AxiosError): string {
  const data = err?.response?.data as unknown;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.message === 'string' && o.message.trim()) return o.message;
    if (typeof o.title === 'string' && o.title.trim()) {
      const detail = typeof o.detail === 'string' ? `: ${o.detail}` : '';
      return `${o.title}${detail}`;
    }
    if (o.errors && typeof o.errors === 'object') {
      const errs = o.errors as Record<string, unknown>;
      for (const k of Object.keys(errs)) {
        const v = errs[k];
        if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') {
          return `${k}: ${v[0]}`;
        }
      }
    }
  }
  if (typeof data === 'string' && data.trim()) return data;
  return err?.message || 'Request failed';
}

/**
 * Replace axios's generic message with the backend's actual reason.
 * Logs once to the console with the request URL + status so the
 * developer can reproduce in Swagger/Postman quickly.
 */
function decorateError(err: AxiosError): AxiosError {
  const status = err?.response?.status;
  const url = err?.config?.url ?? '?';
  const method = (err?.config?.method ?? 'GET').toUpperCase();
  const friendly = extractBackendMessage(err);

  // Console group keeps the noise contained — collapsed by default in
  // most devtools. Attaches the request body so devs can replay it.
  /* eslint-disable no-console */
  try {
    console.groupCollapsed(`[api] ${method} ${url} → ${status ?? 'no-response'}: ${friendly}`);
    if (err?.config?.data) console.log('request', err.config.data);
    if (err?.response?.data) console.log('response', err.response.data);
    console.groupEnd();
  } catch {
    // groupCollapsed isn't available everywhere — swallow.
  }
  /* eslint-enable no-console */

  // Axios's `message` is read-only on its own type but writable in
  // practice (it's a plain Error subclass under the hood).
  try {
    (err as { message: string }).message = friendly;
  } catch {
    // Some environments freeze the error — give up gracefully.
  }
  return err;
}

/* ------------------------------------------------------------------------ */
/* Client factory                                                           */
/* ------------------------------------------------------------------------ */

export function createApiClient(opts: ApiClientOptions = {}): AxiosInstance {
  const leeway = opts.refreshLeewaySeconds ?? 120;
  const instance = axios.create({
    baseURL: opts.baseURL ?? '/api',
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Single-flight refresh — concurrent requests share the same in-flight promise.
  let refreshPromise: Promise<ClientAuthSession> | null = null;

  /**
   * Calls the live `/api/auth/refresh` endpoint with the **session id +
   * refresh token** pair (per the swagger `RefreshRequest` schema). The
   * response is wrapped in `ObjectResponse { success, message, data }`,
   * so we unwrap `data` here before returning.
   */
  async function performRefresh(
    currentRefreshToken: string,
    sessionId: string
  ): Promise<ClientAuthSession> {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const resp = await axios.post<ObjectResponse<ClientAuthSession>>(
          (opts.baseURL ?? '/api').replace(/\/$/, '') + '/auth/refresh',
          { refreshToken: currentRefreshToken, sessionId },
          { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
        );
        const next = unwrap(resp.data);
        opts.setSession?.(next);
        return next;
      } finally {
        // Always clear so the next 401 can trigger a new refresh.
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  }

  instance.interceptors.request.use(async (config) => {
    const cfg = config as RetryConfig;
    if (cfg._skipAuth) return config;

    const session = opts.getSession?.();
    if (!session?.accessToken) return config;

    // Proactive refresh: if access token expires inside the leeway window,
    // refresh first so the outgoing request carries the fresh token.
    let token = session.accessToken;
    if (
      session.refreshToken &&
      session.sessionId &&
      isExpiringSoon(session.expiresAt, leeway)
    ) {
      try {
        const next = await performRefresh(session.refreshToken, session.sessionId);
        token = next.accessToken;
      } catch {
        // Fall through with the stale token; the response interceptor will
        // catch the 401 and trigger logout. Don't break the request here.
      }
    }

    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (err: AxiosError) => {
      const status = err?.response?.status;
      const original = err?.config as RetryConfig | undefined;

      // Reactive refresh on 401 — exactly one retry, then bail to logout.
      if (status === 401 && original && !original._retry && !original._skipAuth) {
        const session = opts.getSession?.();
        if (session?.refreshToken && session.sessionId) {
          original._retry = true;
          try {
            const next = await performRefresh(session.refreshToken, session.sessionId);
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>).Authorization =
              `Bearer ${next.accessToken}`;
            return instance.request(original);
          } catch {
            opts.onUnauthorized?.();
            return Promise.reject(decorateError(err));
          }
        }
        opts.onUnauthorized?.();
      }

      // Centralised error decoration — surface the backend's
      // `ObjectResponse.message` (or ASP.NET ProblemDetails / validation
      // errors dictionary) on the rejected Error so toasts can show
      // *why* the call failed instead of axios's generic
      // "Request failed with status code 400". Without this every
      // CRUD form prints the same useless message regardless of cause.
      // Also logs once to the console with the request payload so the
      // dev can replay in Postman / Swagger.
      // Bug ref: AI Agent Issues #1, #4, #5, #6, #8, #9, #10, #11, #12, #13.
      return Promise.reject(decorateError(err));
    }
  );

  return instance;
}

/* ------------------------------------------------------------------------ */
/* Shared singleton client                                                   */
/* ------------------------------------------------------------------------ */

let _getSession: SessionGetter = () => null;
let _setSession: SessionSetter = () => {};
let _onUnauthorized: UnauthorizedHandler = () => {};
let _refreshLeewaySeconds = 120;

/**
 * Resolve the API base URL with the following priority — first match wins:
 *
 *   1. `window.__KSS_CONFIG__.apiBaseUrl`
 *      → set by `public/runtime-config.js`. EDITABLE POST-DEPLOY without
 *        a rebuild. This is how a customer points the same bundle at
 *        `https://api.kontic.com/api` instead of the seeded default.
 *   2. `import.meta.env.VITE_API_BASE_URL`
 *      → baked into the bundle at build time via `.env.production`.
 *        Used during local dev and as the fallback when the runtime
 *        file is missing.
 *   3. `'/api'` (same-origin)
 *      → final fallback. Works when the API is reverse-proxied by IIS /
 *        the dev server on the same origin.
 *
 * Adding a new runtime knob is just one line in `runtime-config.js`
 * plus a getter here. The bundle never has to change.
 */
const DEFAULT_BASE_URL = '/api';

/** Shape of the runtime-config object set on `window`. */
interface RuntimeConfig {
  apiBaseUrl?: string;
  apiOrigin?: string;
  showCompanyNameInput?: string;
}
function readRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return {};
  return (window as unknown as { __KSS_CONFIG__?: RuntimeConfig }).__KSS_CONFIG__ ?? {};
}

function resolveEnvBaseURL(): string {
  // 1. Runtime config (deploy-time edit, no rebuild).
  const rt = readRuntimeConfig().apiBaseUrl;
  if (rt && typeof rt === 'string' && rt.trim()) return rt.trim();

  // 2. Build-time env var (.env.production VITE_API_BASE_URL).
  try {
    const env = (import.meta as unknown as {
      env?: Record<string, string | boolean | undefined>;
    }).env;
    if (env?.VITE_API_BASE_URL) return String(env.VITE_API_BASE_URL);
  } catch {
    /* fall through */
  }

  // 3. Same-origin fallback.
  return DEFAULT_BASE_URL;
}

/**
 * The shared API client used by all singleton services exported from `@kss/api`.
 * Call {@link configureApi} once at app startup to wire it to the Redux store.
 *
 * Per spec: `Authorization: Bearer {token}` is auto-attached on every request;
 * **TenantId is never sent manually** (the backend reads it from the JWT claims).
 */
export const apiClient: AxiosInstance = createApiClient({
  baseURL: resolveEnvBaseURL(),
  getSession: () => _getSession(),
  setSession: (s) => _setSession(s),
  onUnauthorized: () => _onUnauthorized(),
  refreshLeewaySeconds: _refreshLeewaySeconds,
});

export interface ConfigureApiOptions {
  baseURL?: string;
  getSession?: SessionGetter;
  setSession?: SessionSetter;
  onUnauthorized?: UnauthorizedHandler;
  refreshLeewaySeconds?: number;
}

export function configureApi(opts: ConfigureApiOptions): void {
  if (opts.getSession) _getSession = opts.getSession;
  if (opts.setSession) _setSession = opts.setSession;
  if (opts.onUnauthorized) _onUnauthorized = opts.onUnauthorized;
  if (opts.refreshLeewaySeconds != null) _refreshLeewaySeconds = opts.refreshLeewaySeconds;
  if (opts.baseURL) apiClient.defaults.baseURL = opts.baseURL;
}

export { isExpired, isExpiringSoon };
export type { AxiosInstance, AxiosRequestConfig };
